import PropTypes from 'prop-types';
import { sum } from 'lodash';
import { useMemo } from 'react';
import { Page, View, Text, Image, Document, Font, StyleSheet } from '@react-pdf/renderer';
import { Box, Grid, Stack, Typography } from '@mui/material';
// utils
import { fDate } from '../../../util/formatTime.js';
import { parseISO } from 'date-fns';
// import { fCurrency } from '../../../../util/format-number.js';

// ----------------------------------------------------------------------

Font.register({
  family: 'Ubuntu',
  fontStyle: "normal",
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/roboto/v29/KFOlCnqEu92Fr1MmSU5vAw.ttf',
    },
    {
      src: 'https://fonts.gstatic.com/s/roboto/v29/KFOlCnqEu92Fr1MmWUlvAw.ttf',
      fontWeight: 'bold',
    }
  ],
});

const useStyles = () =>
  useMemo(
    () =>
  StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: 'Ubuntu',
    fontSize: 10,
    lineHeight: 1.3,
    marginBottom: 30
  },

  /* section wrapper */
  section: {
    marginBottom: 2,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
  },

  /* layout helpers */
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  field: {
    padding: 4,
  },

  /* generic field parts */
  label: {
    marginBottom: 2
  },
  line: {
    borderBottom: '1 solid #000',
    minHeight: 12,
    marginTop: 4,
  },
  box: {
    border: '1 solid #000',
    minHeight: 40,
    marginTop: 4,
  },
  signature: {
    border: '1 solid #000',
    height: 60,
    marginTop: 4,
  },
  /* radio row */
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  radioCircle: {
    width: 10,
    height: 10,
    borderRadius: 5,
    border: '1 solid #000',
    marginRight: 2,
  },
  checkboxSquare: {
  width: 10,
  height: 10,
  border: '1 solid #000',
  marginRight: 2,
},
  table: {
    display: 'flex',
    width: 'auto',
  },
  tableRow: {
    flexDirection: 'row',
    borderWidth: 0.5,
    borderStyle: 'solid',
    borderColor: '#000000',
  },
  tableRow1: {
    flexDirection: 'row',
  },
  col2: { width: '20%' },
  colx: { width: '30%' },
  col3: { width: '80%' },
  body1: { fontSize: 10 },
  h4: { fontFamily: 'Helvetica-Bold', fontWeight: 900, fontSize: 16},
   h5: { fontFamily: 'Helvetica-Bold', fontWeight: 900, fontSize: 14},
  h4_cont: { fontFamily: 'Helvetica-Bold', fontWeight: 900, fontSize: 12},
  mb8: { marginBottom: 1 },
    horizontalLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    marginVertical: 10,
    width: '100%',
  },
    noWrapText: {
    flexDirection: 'row',
    flexWrap: 'nowrap', // prevents line breaking inside a container
    fontSize: 12,
    overflow: 'hidden', // hide overflow text
    textOverflow: 'ellipsis', // not supported but semantic
    maxWidth: '100%',
  },
  pageBreak: { pageBreak: 'always' },
}),[]);

const columnWidth = (col) => {
  const num = Number(col);

  switch (true) {
    case num === 6:
      return '50%';
    case num === 4:
      return '33.33%';
    case num === 3:
      return '25%';
    case num === 2:
      return '16.66%';
    case num === 1:
      return '100%';
    case Math.abs(num - 2.4) < 0.001: // handles floating point safely
      return '20%';
    case num === 12:
      return '100%';
    default:
      return '100%';
  }
};

// ----------------------------------------------------------------------

const Field = ({ field, styles, auditingForm }) => {
   const isDateStringValid = (str) => {
        const date = new Date(str);
        return !isNaN(date);
    };

  const value = auditingForm?.section[field.name];
  

  switch (field.type) {
    case 'label':
      return <Text hyphenationCallback={(word) => ['', word, '']} wrap={false}  style={styles.label}>{field.label}</Text>;
    case 'input':
        return (
        <View>
          <Text  hyphenationCallback={(word) => ['', word, '']} wrap={false}  style={styles.label}>{field.label}</Text>
          <Text hyphenationCallback={(word) => ['', word, '']} wrap={false}>{value || ' '}</Text>
        </View>
      );
    case 'datepicker':
      return (
        <View>
          <Text style={styles.label}>{field.label}</Text>
          <Text>{isDateStringValid(value) ? fDate(value) : "" || ' '}</Text>
        </View>
      );

    case 'multiInput':
      return (
        <View>
          <Text hyphenationCallback={(word) => ['', word, '']} wrap={false}  style={styles.label}>{field.label}</Text>
          <Text hyphenationCallback={(word) => ['', word, '']} wrap={false}>{value || ' '}</Text>
        </View>
      );

    case 'radio':
      return (
        <View>
          {field.label && <Text style={styles.label}>{field.label}</Text>}
          <View style={styles.radioRow}>
            {field.option.map((o, i) => (
              <View
                key={i}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginRight: 8,
                }}
              >
                <View
                  style={{
                    ...styles.radioCircle,
                    backgroundColor: o.value == value ? '#000' : '#fff',
                  }}
                />
                <Text>{o.label}</Text>
              </View>
            ))}
          </View>
        </View>
      );

    case 'esignature':
      return (
        <View>
          <Text style={styles.label}>{field.label}</Text>
          {value ? (
            <Image src={value} style={{ height: 60 }} />
          ) : (
            <View style={styles.signature} />
          )}
        </View>
      );
    
    case 'checkbox': {
  // ---- normalise value: could be array, boolean, or undefined ----
        let checkedVals = [];
        if (Array.isArray(value)) checkedVals = value;
        else if (typeof value === 'boolean') checkedVals = value ? [true] : [];

        const isSelected = (optVal) =>
          Array.isArray(checkedVals)
            ? checkedVals.includes(optVal) || checkedVals.includes(true)
            : false;

        return (
          <View>
            {field.label && <Text style={styles.label}>{field.label}</Text>}
            <View
              style={[
                styles.radioRow,
                !field.isRow && { flexDirection: 'column', alignItems: 'flex-start' },
              ]}
            >
              {field.option.map((o, i) => (
                <View
                  key={i}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginRight: field.isRow ? 12 : 0,
                    marginBottom: field.isRow ? 0 : 4,
                  }}
                >
                  <View
                    style={{
                      ...styles.checkboxSquare,
                      backgroundColor: isSelected(o.value) ? '#000' : '#fff',
                    }}
                  />
                  <Text>{o.label}</Text>
                </View>
              ))}
            </View>
          </View>
        );
    }
    case 'select':
        return (
        <View>
          <Text style={styles.label}>{field.label}</Text>
          <Text>{value || ' '}</Text>
        </View>
      );
    default:
      return null;
  }
};

/* ────────────────────────── section renderer ───────────────────── */
const Section = ({ section, styles, auditingForm }) => (
  <View style={styles.section}>
    {section.label?.trim() && (
      <Text style={[styles.sectionLabel, styles.h4_cont]}>{section.label}</Text>
    )}

    <View style={[styles.row]}>
      {section.components.map((field, i) => (
        <View
          key={i}
          style={{ ...styles.field, width: columnWidth(section.column)}}
        >
          <Field field={field} styles={styles} auditingForm={auditingForm} />
        </View>
      ))}
    </View>
  </View>
);

/* ────────────────────────── main component ─────────────────────── */
export default function AuditingPDFView({
  data, 
  auditingForm, 
  companyProfile,
  orientation,
}) {
  const styles = useStyles();
  const logo = companyProfile && `${process.env.PUBLIC_URL}/logo/${companyProfile.companyName}/${companyProfile.logo}`;
  const isPortrait = orientation ? 'portrait' : 'landscape';
  
  return (
    <Document>
      <Page size="A4" orientation={isPortrait} style={styles.page}>
        {/* optional logo */}
        {
          logo ?
          <View style={[styles.table, styles.mb8]}>
              <View style={styles.tableRow1}>
                  <View style={styles.col2}>
                      <Image source={logo} style={{ width: 80, height: 80 }} />
                  </View>
                  <View style={styles.col3}>
                    <Text style={styles.h5}>{companyProfile?.companyName}</Text>
                    <Text style={styles.body1}>{`Address: ${companyProfile?.address}`}</Text>
                    <Text style={styles.body1}>{`Contact No: ${companyProfile?.contactNo}`}</Text>
                    <Text style={styles.body1}>{`Email: ${companyProfile?.email}`}</Text>
                    {
                      companyProfile?.gstNumber !== "NA" ?
                      <Text style={styles.body1}>{`GST Registration No: ${companyProfile?.gstNumber}`}</Text>
                      :
                      ""
                    }
                </View>
                </View>
            </View>
          :
          ""
        }
        <View style={styles.horizontalLine} />
        {data.map((section, i) => (
          <View key={i} break={section?.isPageBreak}>
            <Section key={i} section={section} styles={styles} auditingForm={auditingForm}/>
            {i < data.length - 1 && <View style={styles.horizontalLine} />}
          </View>
        ))}
      </Page>
    </Document>
  );
}

AuditingPDFView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  auditingForm: PropTypes.object,
  companyProfile: PropTypes.shape({
    companyName: PropTypes.string,
    logo: PropTypes.string,
  }),
  orientation: PropTypes.bool
};